import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  message,
  Select,
  TimePicker,
  Typography,
  AutoComplete,
} from "antd";
import { useDispatch } from "react-redux";
import { CreateOrUpdateCommunityLink } from "../redux/actions/WorkshopAction";
import moment from "moment";

const { Title } = Typography;
const { Option } = Select;

const CATEGORY_OPTIONS = ["AI", "English Demo"];
const ROUTE_OPTIONS = ["ggl", "fb", "insta"];

const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const formatWorkshopDate = (date) => {
  if (!date) return "";
  return `${getOrdinal(date.date())} ${date.format("MMMM YYYY")}`;
};

export const parseWorkshopDate = (dateStr) => {
  if (!dateStr) return null;
  const cleaned = String(dateStr).replace(/(\d+)(st|nd|rd|th)/i, "$1");
  const parsed = moment(cleaned, "D MMMM YYYY", true);
  return parsed.isValid() ? parsed : null;
};

export const formatWorkshopTime = (time) => {
  if (!time) return "";
  return `${time.format("hh:mm A")} IST`;
};

export const parseWorkshopTime = (timeStr) => {
  if (!timeStr) return null;
  const cleaned = String(timeStr).replace(/\s*IST\s*/i, "").trim();
  const parsed = moment(cleaned, ["hh:mm A", "h:mm A", "HH:mm"], true);
  return parsed.isValid() ? parsed : null;
};

const getSharedValue = (rows, key) => {
  if (!rows?.length) return undefined;
  const first = rows[0]?.[key];
  const allSame = rows.every((row) => row?.[key] === first);
  return allSame ? first : undefined;
};

const EMPTY_ROWS = [];

const AddCommunityLink = (props) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loader, setLoader] = useState(false);
  const selectedRows = Array.isArray(props.selectedRows) ? props.selectedRows : EMPTY_ROWS;
  const isBulk = Boolean(props.isBulk);
  const isEdit = Boolean(props.value) && !isBulk;
  const isDisabled = isBulk && selectedRows.length === 0;

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        saveCommunityLink(values);
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  const saveCommunityLink = (values) => {
    setLoader(true);

    const payload = {
      category: values.category,
      whatsappLink: values.whatsappLink,
      workshopDate: formatWorkshopDate(values.workshopDate),
      workshopTime: formatWorkshopTime(values.workshopTime),
    };

    if (isEdit) {
      payload.route = values.route;
    } else {
      payload.routes = values.routes;
    }

    const onSuccess = (res) => {
      setLoader(false);
      setVisible(false);
      form.resetFields();
      message.success(res?.message || "Community link saved successfully!", 8);
      if (props.onSaved) {
        props.onSaved();
      }
    };

    const onError = (msg) => {
      setLoader(false);
      message.error(msg, 8);
    };

    dispatch(CreateOrUpdateCommunityLink(payload, onSuccess, onError));
  };

  useEffect(() => {
    if (!visible) return;

    if (isBulk && selectedRows.length) {
      form.setFieldsValue({
        category: getSharedValue(selectedRows, "category") || selectedRows[0].category,
        routes: selectedRows.map((row) => row.route).filter(Boolean),
        whatsappLink: getSharedValue(selectedRows, "whatsappLink") || selectedRows[0].whatsappLink,
        workshopDate: parseWorkshopDate(
          getSharedValue(selectedRows, "workshopDate") || selectedRows[0].workshopDate
        ),
        workshopTime: parseWorkshopTime(
          getSharedValue(selectedRows, "workshopTime") || selectedRows[0].workshopTime
        ),
      });
      return;
    }

    if (props.value) {
      form.setFieldsValue({
        category: props.value.category,
        route: props.value.route,
        routes: props.value.route ? [props.value.route] : [],
        whatsappLink: props.value.whatsappLink,
        workshopDate: parseWorkshopDate(props.value.workshopDate),
        workshopTime: parseWorkshopTime(props.value.workshopTime),
      });
    }
  }, [visible, props.value, form, isBulk, selectedRows]);

  return (
    <>
      <Button
        type={isEdit ? "link" : isBulk ? "default" : "primary"}
        disabled={isDisabled}
        onClick={() => setVisible(true)}
        style={isEdit ? { padding: 0, height: "auto" } : {}}
      >
        {isEdit ? "Edit" : props.name || (isBulk ? "Bulk Edit" : "Add Community Link")}
      </Button>

      <Modal
        title={
          <Title level={4}>
            {isBulk
              ? `Bulk Edit Community Links (${selectedRows.length})`
              : isEdit
              ? "Edit Community Link"
              : "Add Community Link"}
          </Title>
        }
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loader}
            onClick={handleOk}
          >
            {isEdit || isBulk ? "Update" : "Save"}
          </Button>,
        ]}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: "Please select category!" }]}
              >
                <AutoComplete
                  placeholder="Select or type category"
                  options={CATEGORY_OPTIONS.map((category) => ({ value: category }))}
                  filterOption={(input, option) =>
                    (option?.value || "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {isEdit ? (
                <Form.Item
                  name="route"
                  label="Route"
                  rules={[{ required: true, message: "Please enter route!" }]}
                >
                  <AutoComplete
                    placeholder="Select or type route"
                    options={ROUTE_OPTIONS.map((route) => ({ value: route }))}
                    filterOption={(input, option) =>
                      (option?.value || "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="routes"
                  label="Routes"
                  extra={
                    isBulk
                      ? "Add or remove routes. All listed routes will get the same WhatsApp link, date, and time."
                      : "Select existing routes or type a new one and press Enter"
                  }
                  rules={[
                    { required: true, message: "Please add at least one route!" },
                  ]}
                >
                  <Select
                    mode="tags"
                    placeholder="e.g. ggl, fb, insta"
                    tokenSeparators={[","]}
                  >
                    {ROUTE_OPTIONS.map((route) => (
                      <Option key={route} value={route}>
                        {route}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item
            name="whatsappLink"
            label="WhatsApp Link"
            rules={[
              { required: true, message: "Please enter WhatsApp link!" },
              { type: "url", message: "Please enter a valid URL!" },
            ]}
          >
            <Input placeholder="https://chat.whatsapp.com/inviteLink123" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="workshopDate"
                label="Workshop Date"
                rules={[
                  { required: true, message: "Please select workshop date!" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD MMMM YYYY"
                  placeholder="Select date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="workshopTime"
                label="Workshop Time"
                rules={[
                  { required: true, message: "Please select workshop time!" },
                ]}
              >
                <TimePicker
                  style={{ width: "100%" }}
                  format="hh:mm A"
                  use12Hours
                  placeholder="Select time"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AddCommunityLink;
